import { useEffect, useMemo, useState } from "react";
import {
    AlarmClock,
    CalendarClock,
    ChevronDown,
    ChevronUp,
    Download,
    Filter,
    HeartHandshake,
    RotateCcw,
    Search,
    Star,
    TriangleAlert,
} from "lucide-react";
import { Alert, Button, Card, DatePicker, Input, Select, Tabs } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import dayjs, { type Dayjs } from "dayjs";
import CustomerTable from "./CustomerTable";
import Layout from '../../Layout'
import { getCompletedAppointments, CompletedAppointment } from "../../api/AppointmentCare";
import {
    BUCKET_META, CONTACT_RESULT, type CsatFilter, type QuickTab,
    careStateOf, extraOf, matchCsat, matchQuickTab, surveySendsOf, toCsv, useCareExtras,
} from "./careStore";

const { RangePicker } = DatePicker;

const AVATAR_COLORS = ["#E9D5FF", "#DBEAFE", "#DCFCE7", "#FEF3C7", "#FEE2E2", "#CFFAFE"];

/**
 * API chỉ lọc được theo search / is_cared nên tạm tải tối đa FETCH_LIMIT lịch hẹn rồi lọc, đếm, phân trang
 * ở trình duyệt. Khi API hỗ trợ lọc theo ngày / dịch vụ / người làm / CSAT thì chuyển về phân trang server.
 */
const FETCH_LIMIT = 1000;

type RangeKey = "all" | "today" | "7" | "30" | "custom";

interface Filters {
    range: RangeKey;
    custom: [Dayjs, Dayjs] | null;
    service?: string;
    staff?: string;
    cared?: number;
    csat?: CsatFilter;
}
const EMPTY_FILTERS: Filters = { range: "all", custom: null };

// Điểm CSAT trung bình từ 4 tiêu chí khảo sát (1 chữ số thập phân)
const avgCsat = (item: CompletedAppointment): number | null => {
    const r = item.survey_data?.ratings;
    if (!r) return null;
    const vals = [r.quality, r.staff_attitude, r.doctor_ktv, r.facilities].map(Number).filter((v) => v > 0);
    if (!vals.length) return null;
    return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
};

const inRange = (f: Filters, at: string | null) => {
    if (f.range === "all") return true;
    if (!at) return false;
    const d = dayjs(at);
    if (f.range === "custom") return !f.custom || (!d.isBefore(f.custom[0].startOf("day")) && !d.isAfter(f.custom[1].endOf("day")));
    const days = f.range === "today" ? 0 : Number(f.range) - 1;
    return !d.isBefore(dayjs().subtract(days, "day").startOf("day"));
};

const activeCount = (f: Filters) =>
    [f.range !== "all", !!f.service, !!f.staff, f.cared !== undefined, !!f.csat].filter(Boolean).length;

export default function CareListPage() {
    const [showFilter, setShowFilter] = useState(false);
    // Phần tử trên toolbar để bảng đặt nút cài đặt cột vào (qua portal)
    const [columnSettingSlot, setColumnSettingSlot] = useState<HTMLDivElement | null>(null);

    // Danh sách lịch hẹn đã thực hiện (API /appointments/completed)
    const [items, setItems] = useState<CompletedAppointment[]>([]);
    const [total, setTotal] = useState(0);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState<QuickTab>("todo");
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
    // Giá trị nháp trong panel "Bộ lọc nâng cao" (chỉ áp dụng khi bấm "Áp dụng lọc")
    const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
    const extras = useCareExtras();

    const fetchList = () => {
        setLoading(true);
        getCompletedAppointments({ search: search || undefined, page: 1, limit: FETCH_LIMIT }).then((res) => {
            setItems(res.status ? res.items : []);
            setTotal(res.status ? res.total : 0);
            setLoadError(res.status ? null : res.msg || "Không tải được danh sách");
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const allRows = useMemo(() => {
        const now = Date.now();
        return items.map((it, i) => {
            const csat = avgCsat(it);
            const extra = extraOf(extras, it.id);
            return {
                key: it.id,
                id: it.id,
                avatar: (it.customer_name || "?").trim().charAt(0).toUpperCase(),
                color: AVATAR_COLORS[i % AVATAR_COLORS.length],
                name: it.customer_name || "—",
                code: it.code,
                phone: it.customer_phone || "",
                service: (it.services || []).map((s) => s.name).join(", ") || "—",
                services: (it.services || []).map((s) => s.name),
                doctor: it.action_staff_name || "",
                scheduled_at: it.scheduled_at,
                date: it.scheduled_at ? dayjs(it.scheduled_at).format("DD/MM/YYYY") : "—",
                is_cared: it.is_cared,
                status: it.care_status_text || (it.is_cared ? "Đã chăm sóc" : "Chưa chăm sóc"),
                statusColor: it.is_cared ? "green" : "orange",
                cared_at: it.cared_at,
                cared_by_staff_name: it.cared_by_staff_name,
                survey_data: it.survey_data,
                csat,
                extra,
                care: careStateOf({ is_cared: it.is_cared, scheduled_at: it.scheduled_at, csat }, extra, now),
            };
        });
    }, [items, extras]);

    // Lọc nâng cao trước, tab nhanh sau — để số đếm trên tab khớp với bộ lọc đang áp dụng
    const filtered = useMemo(() => allRows.filter((r) =>
        inRange(filters, r.scheduled_at)
        && (!filters.service || r.services.includes(filters.service))
        && (!filters.staff || r.doctor === filters.staff)
        && (filters.cared === undefined || Number(r.is_cared) === filters.cared)
        && (!filters.csat || matchCsat(filters.csat, r.csat))
    ), [allRows, filters]);

    const tabCount = (t: QuickTab) => filtered.filter((r) => matchQuickTab(t, r.care)).length;
    const rows = useMemo(() => {
        const list = filtered.filter((r) => matchQuickTab(tab, r.care));
        // Việc gấp lên đầu: cảnh báo → trễ hạn nhiều → đến hạn sớm
        const rank = (r: typeof list[number]) => (r.care.warning ? 0 : r.care.bucket === "overdue" ? 1 : r.care.bucket === "due" ? 2 : 3);
        return [...list].sort((a, b) => rank(a) - rank(b) || b.care.lateDays - a.care.lateDays || (a.care.dueAt ?? 0) - (b.care.dueAt ?? 0));
    }, [filtered, tab]);

    useEffect(() => { setPage(1); }, [tab, filters, search]);

    const serviceOptions = useMemo(() => Array.from(new Set(allRows.flatMap((r) => r.services))).sort().map((s) => ({ label: s, value: s })), [allRows]);
    const staffOptions = useMemo(() => Array.from(new Set(allRows.map((r) => r.doctor).filter(Boolean))).sort().map((s) => ({ label: s, value: s })), [allRows]);

    const rated = filtered.filter((r) => r.csat !== null);
    const csatAvg = rated.length ? Math.round((rated.reduce((s, r) => s + (r.csat || 0), 0) / rated.length) * 10) / 10 : null;
    const caredCount = tabCount("cared");
    const stats = [
        { key: "todo" as QuickTab, title: "CẦN CHĂM SÓC", value: tabCount("todo"), color: "#1890ff", icon: <HeartHandshake size={22} />, sub: `${tabCount("overdue")} trễ hạn` },
        { key: "followup" as QuickTab, title: "HẸN CHĂM SÓC LẠI", value: tabCount("followup"), color: "#8b5cf6", icon: <CalendarClock size={22} />, sub: "đã đến ngày hẹn" },
        { key: "warning" as QuickTab, title: "CẢNH BÁO / BIẾN CHỨNG", value: tabCount("warning"), color: "#ef4444", danger: tabCount("warning") > 0, icon: <TriangleAlert size={22} />, sub: "kể cả CSAT thấp" },
        { key: "cared" as QuickTab, title: "ĐÃ CHĂM SÓC", value: caredCount, color: "#22c55e", icon: <Star size={22} />, sub: `CSAT TB ${csatAvg ?? "—"} · ${filtered.length ? Math.round((caredCount / filtered.length) * 100) : 0}% hoàn thành` },
    ];

    const exportCsv = () => {
        const header = ["Mã lịch hẹn", "Khách hàng", "SĐT", "Dịch vụ", "Người thực hiện", "Ngày làm", "Tình trạng", "Hạn chăm sóc", "CSAT", "Cảnh báo", "Hẹn chăm sóc lại", "Lần liên hệ gần nhất", "Gửi link khảo sát", "Giới thiệu bạn bè", "Người chăm sóc"];
        const body = rows.map((r) => [
            r.code, r.name, r.phone, r.service, r.doctor, r.date,
            BUCKET_META[r.care.bucket].label,
            r.care.dueAt ? dayjs(r.care.dueAt).format("DD/MM/YYYY") : "",
            r.csat ?? "",
            r.care.openFlags.map((f) => f.note).join("; ") || (r.care.lowCsat ? "CSAT thấp" : ""),
            r.extra.followUp ? `${dayjs(r.extra.followUp.at).format("DD/MM/YYYY")} ${r.extra.followUp.note}` : "",
            r.extra.attempts[0] ? `${dayjs(r.extra.attempts[0].at).format("DD/MM HH:mm")} ${CONTACT_RESULT[r.extra.attempts[0].result].label}` : "",
            surveySendsOf(r.extra).length ? `${surveySendsOf(r.extra).length} lần, gần nhất ${dayjs(surveySendsOf(r.extra)[0].at).format("DD/MM/YYYY")}` : "",
            r.extra.referral === true ? "Có" : r.extra.referral === false ? "Không" : "",
            r.cared_by_staff_name || "",
        ]);
        const blob = new Blob([toCsv([header, ...body])], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `danh-sach-cham-soc-${dayjs().format("YYYYMMDD-HHmm")}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const nFilters = activeCount(filters);

    return (
        <Layout>

            {/* Header */}

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

                <h2 className="text-sm sm:text-3xl font-bold text-000000e0">
                    Danh sách cần chăm sóc
                </h2>

            </div>

            {/* Summary — bấm thẻ để lọc nhanh */}

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-5 mt-6">

                {stats.map((item) => (
                    <Card
                        key={item.title}
                        hoverable
                        onClick={() => setTab(item.key)}
                        className={`rounded-2xl border transition-all ${item.danger ? "border-red-300" : ""} ${tab === item.key ? "ring-2 ring-offset-1" : ""} dark:bg-[#262626] dark:border-neutral-700`}
                        style={tab === item.key ? { ["--tw-ring-color" as any]: item.color } : undefined}
                        styles={{ body: { padding: 18 } }}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                                <p className={`text-xs sm:text-sm font-medium ${item.danger ? "text-red-500" : "text-slate-500"}`}>
                                    {item.title}
                                </p>
                                <h2 className={`text-3xl sm:text-4xl font-bold mt-2 ${item.danger ? "text-red-500" : "text-000000e0"}`}>
                                    {item.value}
                                </h2>
                                <p className="text-xs text-slate-400 mt-1 truncate">{item.sub}</p>
                            </div>
                            <div
                                className="w-12 h-12 rounded-full items-center justify-center shrink-0 hidden sm:flex"
                                style={{ background: `${item.color}15`, color: item.color }}
                            >
                                {item.icon}
                            </div>
                        </div>
                    </Card>
                ))}

            </div>

            {/* Content */}

            <Card
                className="mt-6 rounded-2xl dark:bg-[#262626] dark:border-neutral-700"
                styles={{ body: { padding: 16 } }}
            >

                <div className="flex flex-col lg:flex-row lg:justify-between gap-4 lg:items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <Input
                            placeholder="Tìm mã lịch hẹn, tên, SĐT khách hàng..."
                            prefix={<Search size={18} />}
                            className="w-full lg:w-[320px] h-[32px]"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onPressEnter={() => setSearch(searchInput.trim())}
                            allowClear
                            onClear={() => setSearch("")}
                        />
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <Button icon={<Download size={16} />} onClick={exportCsv} disabled={!rows.length}>
                            Xuất Excel
                        </Button>
                        <Button
                            onClick={() => { setDraft(filters); setShowFilter(!showFilter); }}
                            icon={<Filter size={16} />}
                            type={nFilters ? "primary" : "default"}
                            ghost={!!nFilters}
                        >
                            Bộ lọc nâng cao{nFilters ? ` (${nFilters})` : ""}
                            {showFilter ? (
                                <ChevronUp className="ml-2" size={16} />
                            ) : (
                                <ChevronDown className="ml-2" size={16} />
                            )}
                        </Button>
                        {/* Chỗ đặt nút cài đặt cột của bảng (chỉ bản desktop có bảng) */}
                        <div ref={setColumnSettingSlot} className="hidden lg:flex" />
                    </div>
                </div>

                <AnimatePresence>
                    {showFilter && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden"
                        >
                            <div className="border-t mt-5 pt-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">

                                    <div>
                                        <label className="text-sm mb-2 block text-000000e0">
                                            Thời gian làm dịch vụ
                                        </label>
                                        <Select
                                            className="w-full"
                                            value={draft.range}
                                            onChange={(v) => setDraft((d) => ({ ...d, range: v, custom: v === "custom" ? d.custom : null }))}
                                            options={[
                                                { label: "Tất cả thời gian", value: "all" },
                                                { label: "Hôm nay", value: "today" },
                                                { label: "7 ngày qua", value: "7" },
                                                { label: "30 ngày qua", value: "30" },
                                                { label: "Tuỳ chọn khoảng ngày", value: "custom" },
                                            ]}
                                        />
                                        {draft.range === "custom" && (
                                            <RangePicker
                                                className="w-full mt-2"
                                                format="DD/MM/YYYY"
                                                value={draft.custom}
                                                onChange={(v) => setDraft((d) => ({ ...d, custom: v && v[0] && v[1] ? [v[0], v[1]] : null }))}
                                            />
                                        )}
                                    </div>

                                    <div>
                                        <label className="text-sm mb-2 block text-000000e0">
                                            Dịch vụ đã làm
                                        </label>
                                        <Select
                                            className="w-full"
                                            placeholder="Tất cả dịch vụ"
                                            allowClear
                                            showSearch
                                            value={draft.service}
                                            onChange={(v) => setDraft((d) => ({ ...d, service: v }))}
                                            options={serviceOptions}
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm mb-2 block text-000000e0">
                                            Người thực hiện
                                        </label>
                                        <Select
                                            className="w-full"
                                            placeholder="Tất cả Bác sĩ / KTV"
                                            allowClear
                                            showSearch
                                            value={draft.staff}
                                            onChange={(v) => setDraft((d) => ({ ...d, staff: v }))}
                                            options={staffOptions}
                                            notFoundContent="Chưa có dữ liệu người thực hiện"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm mb-2 block text-000000e0">
                                            Trạng thái chăm sóc
                                        </label>
                                        <Select
                                            className="w-full"
                                            placeholder="Tất cả trạng thái"
                                            allowClear
                                            value={draft.cared}
                                            onChange={(v) => setDraft((d) => ({ ...d, cared: v }))}
                                            options={[
                                                { label: "Đã chăm sóc", value: 1 },
                                                { label: "Chưa chăm sóc", value: 0 },
                                            ]}
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm mb-2 block text-000000e0">
                                            Mức độ hài lòng (CSAT)
                                        </label>
                                        <Select
                                            className="w-full"
                                            placeholder="Tất cả đánh giá"
                                            allowClear
                                            value={draft.csat}
                                            onChange={(v) => setDraft((d) => ({ ...d, csat: v }))}
                                            options={[
                                                { label: "Hài lòng (từ 4 sao)", value: "high" },
                                                { label: "Bình thường (3 – dưới 4 sao)", value: "mid" },
                                                { label: "Không hài lòng (dưới 3 sao)", value: "low" },
                                                { label: "Chưa đánh giá", value: "none" },
                                            ]}
                                        />
                                    </div>

                                </div>

                                <div className="flex justify-end gap-3 mt-5">

                                    <Button
                                        icon={<RotateCcw size={16} />}
                                        onClick={() => { setDraft(EMPTY_FILTERS); setFilters(EMPTY_FILTERS); }}
                                    >
                                        Xóa bộ lọc
                                    </Button>

                                    <Button
                                        type="primary"
                                        onClick={() => setFilters(draft)}
                                    >
                                        Áp dụng lọc
                                    </Button>

                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <Tabs
                    className="mt-3"
                    activeKey={tab}
                    onChange={(k) => setTab(k as QuickTab)}
                    items={[
                        { key: "todo", label: <span className="flex items-center gap-1"><AlarmClock size={14} /> Cần chăm sóc ({tabCount("todo")})</span> },
                        { key: "overdue", label: <span className="text-red-500">Trễ hạn ({tabCount("overdue")})</span> },
                        {
                            key: "warning",
                            label: (
                                <span className="text-red-500 flex items-center gap-1">
                                    <TriangleAlert size={14} />
                                    Cảnh báo ({tabCount("warning")})
                                </span>
                            ),
                        },
                        { key: "followup", label: `Hẹn chăm sóc lại (${tabCount("followup")})` },
                        { key: "awaiting", label: <span className="text-purple-600">Chờ khách đánh giá ({tabCount("awaiting")})</span> },
                        { key: "cared", label: `Đã chăm sóc (${caredCount})` },
                        { key: "all", label: `Tất cả (${filtered.length})` },
                    ]}
                />

                {loadError && <Alert type="error" showIcon className="mb-3" message={loadError} action={<Button size="small" onClick={fetchList}>Thử lại</Button>} />}
                {total > items.length && (
                    <Alert
                        type="warning"
                        showIcon
                        className="mb-3"
                        message={`Đang hiển thị ${items.length}/${total} lịch hẹn gần nhất. Hãy tìm theo tên / SĐT để thu hẹp danh sách.`}
                    />
                )}

                <div>
                    <CustomerTable
                        data={rows.slice((page - 1) * pageSize, page * pageSize)}
                        allData={allRows}
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize,
                            total: rows.length,
                            onChange: (p: number, ps: number) => { setPage(ps !== pageSize ? 1 : p); setPageSize(ps); },
                        }}
                        onRefresh={fetchList}
                        columnSettingSlot={columnSettingSlot}
                    />
                </div>

            </Card>

        </Layout>
    );
}
